import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { parseServerError } from "utils/errors";
import { fetchUser } from "../../../../redux/features/user/userSlice";
import {
  createSandboxApi,
  deleteSandboxApi,
  getSandboxInfoApi,
  switchBackFromSandboxApi,
  switchToSandboxApi,
} from "../api/sandboxApi";
import { normalizeSandboxInfo } from "./sandboxModel";
import {
  clearSandboxAccessRecoveryMark,
  hasSandboxSessionMark,
  setSandboxSessionMark,
} from "./sandboxSession";

function rejectMessage(error) {
  return error?.message || "Error";
}

export const fetchSandboxInfo = createAsyncThunk(
  "sandbox/fetchInfo",
  async (_, { rejectWithValue, getState }) => {
    try {
      const normalized = normalizeSandboxInfo(await getSandboxInfoApi());
      const companyFlag = getState().user?.user?.company?.is_sandbox;
      if (companyFlag === true || companyFlag === false) {
        normalized.isSandboxSession = companyFlag;
      }
      return normalized;
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const createSandbox = createAsyncThunk(
  "sandbox/create",
  async (payload, { rejectWithValue }) => {
    try {
      return await createSandboxApi(payload);
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const deleteSandbox = createAsyncThunk(
  "sandbox/delete",
  async (_, { rejectWithValue }) => {
    try {
      return await deleteSandboxApi();
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const switchToSandbox = createAsyncThunk(
  "sandbox/switch",
  async (_, { rejectWithValue }) => {
    try {
      return await switchToSandboxApi();
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const switchBackFromSandbox = createAsyncThunk(
  "sandbox/switchBack",
  async (_, { rejectWithValue }) => {
    try {
      return await switchBackFromSandboxApi();
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

const initialState = {
  sandboxes: [],
  isSandboxSession: hasSandboxSessionMark(),
  originalCompanyName: null,
  loaded: false,
  fetching: false,
  creating: false,
  deleting: false,
  switching: false,
  error: null,
};

const sandboxSlice = createSlice({
  name: "sandbox",
  initialState,
  reducers: {
    clearSandboxError(state) {
      state.error = null;
    },
    setSandboxError(state, action) {
      state.error = parseServerError(action.payload) || "Error";
      state.switching = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        clearSandboxAccessRecoveryMark();
        const companyFlag = action.payload?.company?.is_sandbox;
        if (companyFlag === true || companyFlag === false) {
          state.isSandboxSession = companyFlag;
          setSandboxSessionMark(companyFlag);
        }
      })
      .addCase(fetchSandboxInfo.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(fetchSandboxInfo.fulfilled, (state, action) => {
        state.fetching = false;
        state.loaded = true;
        state.sandboxes = action.payload.sandboxes;
        state.isSandboxSession = action.payload.isSandboxSession;
        state.originalCompanyName = action.payload.originalCompanyName;
        setSandboxSessionMark(action.payload.isSandboxSession);
      })
      .addCase(fetchSandboxInfo.rejected, (state, action) => {
        state.fetching = false;
        state.loaded = true;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(createSandbox.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createSandbox.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createSandbox.rejected, (state, action) => {
        state.creating = false;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(deleteSandbox.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteSandbox.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteSandbox.rejected, (state, action) => {
        state.deleting = false;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(switchToSandbox.pending, (state) => {
        state.switching = true;
        state.error = null;
      })
      .addCase(switchToSandbox.fulfilled, (state) => {
        state.switching = false;
        state.isSandboxSession = true;
        setSandboxSessionMark(true);
      })
      .addCase(switchToSandbox.rejected, (state, action) => {
        state.switching = false;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(switchBackFromSandbox.pending, (state) => {
        state.switching = true;
        state.error = null;
      })
      .addCase(switchBackFromSandbox.fulfilled, (state) => {
        state.switching = false;
        state.isSandboxSession = false;
        setSandboxSessionMark(false);
      })
      .addCase(switchBackFromSandbox.rejected, (state, action) => {
        state.switching = false;
        state.error = parseServerError(action.payload ?? action.error);
      });
  },
});

export const { clearSandboxError, setSandboxError } = sandboxSlice.actions;
export default sandboxSlice.reducer;
